import { useState, useEffect, useCallback } from "react";
import { ArrowLeft, MapPin, Leaf, BarChart3, Loader2, Star, StarOff, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import ParcelDetail from "@/components/ParcelDetail";
import { toast } from "sonner";
import { authFetch } from "@/lib/api";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProDashboard({ user, onBack, onPhyto, isPro }) {
  const [data, setData] = useState({ parcels: [], stats: { count: 0, total_ha: 0, cultures: {} } });
  const [loading, setLoading] = useState(true);
  const [selectedParcel, setSelectedParcel] = useState(null);
  const [search, setSearch] = useState("");

  const fetchFollowed = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE}/parcels/followed`);
      if (res.ok) setData(await res.json());
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchFollowed(); }, [fetchFollowed]);

  const handleUnfollow = async (parcelId) => {
    try {
      await authFetch(`${API_BASE}/parcels/${parcelId}/follow`, { method: "DELETE" });
      toast.success("Parcelle retirée du suivi");
      fetchFollowed();
      if (selectedParcel?.parcel_id === parcelId) setSelectedParcel(null);
    } catch { toast.error("Erreur"); }
  };

  const filtered = data.parcels.filter(p => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (p.name || "").toLowerCase().includes(s) ||
      (p.commune || "").toLowerCase().includes(s) ||
      (p.culture_type || "").toLowerCase().includes(s) ||
      (p.parcel_id || "").toLowerCase().includes(s);
  });

  const cultureEntries = Object.entries(data.stats.cultures).sort((a, b) => b[1] - a[1]);

  if (selectedParcel) {
    return (
      <div className="h-full flex flex-col bg-white" data-testid="pro-dashboard-detail">
        <ParcelDetail
          parcel={selectedParcel}
          onClose={() => setSelectedParcel(null)}
          onRefresh={fetchFollowed}
          user={user}
          onPhyto={onPhyto}
          isPro={isPro}
        />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white" data-testid="pro-dashboard">
      {/* Header */}
      <div className="shrink-0 p-4 border-b border-black/5">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={onBack} data-testid="pro-dashboard-back">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', color: '#1A1A1A' }}>
              Mes parcelles suivies
            </h2>
            <p className="text-xs mt-0.5" style={{ color: '#7A7A7A' }}>Tableau de bord PRO</p>
          </div>
          <img src="/veyer-ciel-pro-logo.png" alt="PRO" className="h-8 w-8 rounded-lg object-contain" style={{ background: '#0A0A0A' }} />
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#2D6A4F' }} />
            </div>
          ) : (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-3 gap-2">
                <div className="p-3 rounded-xl border border-black/5" style={{ background: '#2D6A4F08' }}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <MapPin className="w-3.5 h-3.5" style={{ color: '#2D6A4F' }} />
                    <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: '#7A7A7A' }}>Parcelles</span>
                  </div>
                  <p className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: '#1A1A1A' }}>{data.stats.count}</p>
                </div>
                <div className="p-3 rounded-xl border border-black/5" style={{ background: '#E07A5F08' }}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <BarChart3 className="w-3.5 h-3.5" style={{ color: '#E07A5F' }} />
                    <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: '#7A7A7A' }}>Hectares</span>
                  </div>
                  <p className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: '#1A1A1A' }}>{data.stats.total_ha}</p>
                </div>
                <div className="p-3 rounded-xl border border-black/5" style={{ background: '#8B5CF608' }}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <Leaf className="w-3.5 h-3.5" style={{ color: '#8B5CF6' }} />
                    <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: '#7A7A7A' }}>Cultures</span>
                  </div>
                  <p className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: '#1A1A1A' }}>{cultureEntries.length}</p>
                </div>
              </div>

              {/* Cultures Breakdown */}
              {cultureEntries.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {cultureEntries.slice(0, 8).map(([name, count]) => (
                    <Badge key={name} variant="outline" className="text-[10px] font-medium py-0.5 px-2 border-black/10" style={{ color: '#4A4A4A' }}>
                      {name} ({count})
                    </Badge>
                  ))}
                  {cultureEntries.length > 8 && (
                    <Badge variant="outline" className="text-[10px] font-medium py-0.5 px-2 border-black/10" style={{ color: '#7A7A7A' }}>
                      +{cultureEntries.length - 8} autres
                    </Badge>
                  )}
                </div>
              )}

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: '#7A7A7A' }} />
                <Input placeholder="Rechercher une parcelle..." value={search} onChange={e => setSearch(e.target.value)}
                  className="pl-9 h-9 text-xs" data-testid="pro-dashboard-search" />
              </div>

              {/* Parcels List */}
              {filtered.length === 0 ? (
                <div className="text-center py-12">
                  <Star className="w-10 h-10 mx-auto mb-3 opacity-20" />
                  <p className="text-sm font-medium" style={{ color: '#4A4A4A' }}>
                    {data.parcels.length === 0 ? "Aucune parcelle suivie" : "Aucun resultat"}
                  </p>
                  <p className="text-xs mt-1" style={{ color: '#7A7A7A' }}>
                    {data.parcels.length === 0 ? "Cliquez sur une parcelle de la carte et appuyez sur \"Suivre\"" : "Modifiez votre recherche"}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filtered.map(p => (
                    <div key={p.parcel_id} className="p-3 rounded-xl border border-black/5 hover:border-black/10 transition-all cursor-pointer group"
                      onClick={() => setSelectedParcel(p)} data-testid={`pro-parcel-${p.parcel_id}`}>
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: '#2D6A4F12' }}>
                          <MapPin className="w-4 h-4" style={{ color: '#2D6A4F' }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold truncate" style={{ color: '#1A1A1A' }}>{p.name || p.parcel_id}</p>
                            <Badge variant="outline" className="text-[9px] py-0 px-1.5 shrink-0 border-black/10" style={{ color: '#7A7A7A' }}>
                              {p.surface_ha} ha
                            </Badge>
                          </div>
                          <p className="text-[11px] mt-0.5" style={{ color: '#4A4A4A' }}>{p.commune} — {p.culture_type}</p>
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); handleUnfollow(p.parcel_id); }}
                          className="p-1.5 rounded-lg hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                          data-testid={`unfollow-${p.parcel_id}`} title="Retirer du suivi">
                          <StarOff className="w-4 h-4" style={{ color: '#EF4444' }} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
